import React from 'react'
import clsx from 'clsx'
import NextImage from 'next/image'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'

import { useProfile } from 'app/ProfileRevampPage/hooks/useProfile'
import { useSETrackEvent } from 'app/ProfileRevampPage/hooks/useSETrackEvent'
import { getImageDimensions } from 'utils/image'

import type { TProfileCommunity } from 'types/profile'

type TCommunityItemProps = React.HTMLAttributes<HTMLAnchorElement> &
  TProfileCommunity
const CommunityItem = ({
  id,
  name,
  thumbnailUrl,
  ...props
}: TCommunityItemProps) => {
  const [imageWidth, setImageWidth] = React.useState(null)
  const [imageHeight, setImageHeight] = React.useState(null)
  const link = `/komunitas/${id}?from=profil`

  const getDimensions = async () => {
    try {
      const { width, height } = await getImageDimensions(thumbnailUrl)
      setImageWidth(width)
      setImageHeight(height)
    } catch (err) {}
  }
  React.useEffect(() => {
    if (Boolean(thumbnailUrl)) {
      getDimensions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbnailUrl])

  const imageLoaded =
    Boolean(thumbnailUrl) && Boolean(imageWidth) && Boolean(imageHeight)

  return (
    <a
      href={link}
      className="relative flex w-1/3 flex-col mb-3 border border-subdued rounded-[4px]"
      {...props}
    >
      <div
        className={clsx(
          'aspect-square relative rounded-t-[4px] flex justify-center items-center overflow-hidden',
          !imageLoaded && 'bg-gray-50'
        )}
      >
        {imageLoaded && (
          <NextImage
            className={clsx(
              'rounded-t-[4px] absolute left-0 right-0 top-0 bottom-0',
              imageWidth > imageHeight && 'object-contain',
              imageWidth <= imageHeight && 'object-cover'
            )}
            style={{ width: '100%' }}
            height="300px"
            width="300px"
            src={thumbnailUrl}
            alt={name}
          />
        )}
      </div>
      <div className="py-1 px-[6px] rounded-b-[4px]">
        <Text
          className="break-all overlay-link line-clamp-2 text-ellipsis"
          variant="helper"
        >
          {name}
        </Text>
      </div>
    </a>
  )
}

const CommunityCard = () => {
  const { isCurrentUser, profileData, profileId, hasCommunity } = useProfile()
  const { SETrackClickCommunityItem, SETrackClickCommunityShowAll } =
    useSETrackEvent()

  if (!hasCommunity) {
    return null
  }

  return (
    <div className="mb-6">
      <Text
        data-testid="profile-community-title"
        variant="subheading"
        className="mb-3"
      >
        KOMUNITAS
      </Text>

      {hasCommunity && (
        <>
          <div className="flex gap-3">
            {(profileData?.community ?? []).slice(0, 3).map((community) => {
              return (
                <CommunityItem
                  key={`community-item-${community.id}`}
                  data-testid="profile-community-item"
                  id={community.id}
                  name={community.name}
                  thumbnailUrl={community.thumbnailUrl}
                  onClick={SETrackClickCommunityItem}
                />
              )
            })}
          </div>

          <Button
            data-testid="profile-community-view-all"
            as="a"
            className="mt-3"
            color="white"
            fullWidth
            size="md"
            variant="solid"
            href={
              isCurrentUser
                ? `/komunitas/yang-anda-ikuti`
                : `/komunitas/diikuti/${profileId}`
            }
            onClick={SETrackClickCommunityShowAll}
          >
            Lihat semua komunitas {isCurrentUser ? 'Anda' : 'yang diikuti'}
          </Button>
        </>
      )}

      {!hasCommunity && (
        <Text
          data-testid="profile-community-empty"
          variant="body"
          color="disabled"
          className="mb-2 whitespace-pre-line"
        >
          Belum ada
        </Text>
      )}
    </div>
  )
}

export default CommunityCard
